import React from 'react';
import ContentPageTemplate from '@/components/ContentPageTemplate';

export default function CostQuestionPage() {
  const pageData = {
    title: "How Much Do Impact Windows Cost in Miami?",
    metaDescription: "Real Miami impact window pricing: $930–$1,003 per window installed, $15,000–$40,000+ full-house projects. See Good/Better/Premium cost breakdowns.",
    canonicalPath: "/how-much-do-impact-windows-cost-miami",
    
    directAnswer: (
      <>
        <p>
          Here's a real example based on a common window size: For a 37\" x 51\" impact window (laminated glass, aluminum frame, gray tint): Good (ECO): $930 installed, Better (Mr Glass): $960 installed, Premium (ES Windows – Elite): $1,003 installed. Larger windows, custom shapes, or higher design pressure requirements will increase the cost. Most homes aren't made up of identical windows — that's why we price the entire project rather than just per window. A full-house replacement typically ranges from <strong>$15,000 to $40,000+</strong> depending on window count, sizes, and product quality.
        </p>
      </>
    ),

    sections: [
      {
        title: "Cost Per Window in Miami",
        testId: "per-window-cost",
        content: (
          <p>
            Standard impact window pricing in South Florida:
          </p>
        ),
        bullets: [
          "Small windows (24\"×48\"): $800–$1,200 installed",
          "Medium windows (36\"×60\"): $950–$1,400 installed",
          "Large windows (48\"×72\"): $1,100–$1,600 installed",
          "Picture windows (60\"×60\"): $1,300–$1,800 installed",
          "Custom/arched windows: $1,500–$4,500+ installed"
        ]
      },

      {
        title: "Total Project Cost Examples",
        testId: "project-examples",
        content: (
          <p>
            Real-world pricing for Miami homes:
          </p>
        ),
        bullets: [
          "Small home (8–10 windows): $10,000–$15,000",
          "Medium home (12–15 windows): $18,000–$25,000",
          "Large home (18–22 windows): $28,000–$38,000",
          "Full house with doors (20+ openings): $35,000–$50,000+"
        ]
      },

      {
        title: "What's Included in the Price?",
        testId: "included-cost",
        bullets: [
          "Professional installation by licensed contractors",
          "Complete permit process (application, fees, inspections)",
          "Removal and disposal of old windows",
          "Interior and exterior trim/finishing",
          "Final inspection coordination",
          "Product warranty (10–lifetime depending on tier)"
        ],
        highlight: "All our quotes include everything needed for a complete, code-compliant installation."
      },

      {
        title: "Factors That Affect Your Cost",
        testId: "cost-factors",
        bullets: [
          <><strong>Window size and shape:</strong> Larger openings and custom shapes (arches, angles) increase cost</>,
          <><strong>Product tier:</strong> ECO (Good), Mr Glass (Better), and ES Windows (Premium) each vary in design, performance, and price</>,
          <><strong>Number of openings:</strong> Larger projects typically reduce the cost per opening</>,
          <><strong>Installation complexity:</strong> Second-story work, limited access, or structural modifications can increase labor</>,
          <><strong>Glass type and upgrades:</strong> Options like Low-E coatings, tint, and higher-performance laminated glass can increase cost depending on the system selected</>
        ]
      },

      {
        title: "How to Get Your Exact Quote",
        testId: "get-quote",
        content: (
          <p>
            Get accurate pricing without an in-home sales visit:
          </p>
        ),
        bullets: [
          "Upload photos of your windows (2-minute process)",
          "Answer a few questions about your project",
          "Receive Good/Better/Premium options within 24 hours",
          "Compare pricing and features side-by-side",
          "Choose when you're ready, no pressure"
        ]
      }
    ],

    relatedLinks: [
      {
        title: "Complete Pricing Guide",
        description: "See detailed cost breakdown",
        url: "/impact-window-cost-miami"
      },
      {
        title: "Product Tiers Explained",
        description: "Good vs Better vs Premium",
        url: "/impact-window-cost-miami#product-tiers"
      },
      {
        title: "Permit Requirements",
        description: "Understand permit process and costs",
        url: "/impact-window-permits-miami-dade"
      }
    ],

    ctaText: "Get Your Exact Quote",
    ctaSubtext: "Upload photos and receive customized pricing within 24 hours",

    breadcrumbs: [
      { label: "Home", link: "/" },
      { label: "How Much Do Impact Windows Cost?" }
    ]
  };

  return <ContentPageTemplate {...pageData} />;
}
